import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggerService } from '../../logging/services/logger.service';
import { AppError, BackendErrorResponse } from '../models/error.model';

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {
  private readonly logger = inject(LoggerService);

  handleError(error: unknown): AppError {
    let appError: AppError;

    // Handle view transition errors (these are non-critical)
    if (this.isViewTransitionError(error)) {
      this.logger.debug('View transition aborted (non-critical error)', 'ErrorHandler');
      appError = {
        code: 'VIEW_TRANSITION_ABORTED',
        message: 'Navigation cancelled',
        details: { originalError: error },
        timestamp: new Date()
      };
      // Don't log view transition errors as actual errors
      return appError;
    }

    if (error instanceof HttpErrorResponse) {
      appError = this.handleHttpError(error);
    } else if (error instanceof Error) {
      appError = this.handleJavaScriptError(error);
    } else {
      appError = this.handleUnknownError(error);
    }

    this.logger.error(appError.message, 'ErrorHandler', appError);
    return appError;
  }

  private isViewTransitionError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const errorObj = error as Record<string, unknown>;
    const message = errorObj['message'] as string || '';
    const name = errorObj['name'] as string || '';
    return (
      message.includes('Transition was aborted') ||
      message.includes('ViewTransition') ||
      name === 'AbortError' ||
      (errorObj as { statusText?: string }).statusText === 'abort'
    );
  }

  private handleHttpError(error: HttpErrorResponse): AppError {
    const timestamp = new Date();
    const backendError = error.error as BackendErrorResponse | null;

    const getBackendMessage = (): string | undefined => {
      return backendError?.message
        || backendError?.error
        || error.message
        || undefined;
    };

    const getBackendCode = (): string => {
      return backendError?.errorCode
        || `HTTP_${error.status}`
        || 'HTTP_ERROR';
    };

    const getValidationMessage = (): string | undefined => {
      if (backendError?.errors && Array.isArray(backendError.errors) && backendError.errors.length > 0) {
        return backendError.errors.join('. ');
      }
      return undefined;
    };

    switch (error.status) {
      case 400:
        return {
          code: getBackendCode(),
          message: getValidationMessage() || getBackendMessage() || 'Bad request. Please check your input.',
          details: backendError,
          timestamp
        };

      case 401:
        return {
          code: getBackendCode(),
          message: getBackendMessage() || 'Session expired. Please login again.',
          details: backendError,
          timestamp
        };

      case 403:
        return {
          code: getBackendCode(),
          message: getBackendMessage() || 'You do not have permission to perform this action.',
          details: backendError,
          timestamp
        };

      case 404:
        return {
          code: getBackendCode(),
          message: getBackendMessage() || 'The requested resource was not found.',
          details: backendError,
          timestamp
        };

      case 409:
        return {
          code: getBackendCode(),
          message: getBackendMessage() || 'A conflict occurred. The resource may already exist.',
          details: backendError,
          timestamp
        };

      case 423:
        return {
          code: getBackendCode(),
          message: getBackendMessage() || 'Your account is temporarily locked. Please try again later.',
          details: backendError,
          timestamp
        };

      case 429:
        const retryAfter = backendError?.message?.match(/(\d+) minute/)?.[1];
        return {
          code: 'RATE_LIMIT_EXCEEDED',
          message: retryAfter
            ? `Too many attempts. Please try again in ${retryAfter} minute(s).`
            : (getBackendMessage() || 'Too many requests. Please try again later.'),
          details: backendError,
          timestamp
        };

      case 500:
        const serverMessage = getBackendMessage();
        const showServerMessage = serverMessage
          && serverMessage !== 'An internal server error occurred'
          && !serverMessage.includes('stack trace');

        return {
          code: getBackendCode(),
          message: showServerMessage
            ? serverMessage
            : 'An unexpected error occurred. Please try again later.',
          details: backendError,
          timestamp
        };

      default:
        return {
          code: getBackendCode(),
          message: getBackendMessage() || error.statusText || 'An error occurred',
          details: backendError,
          timestamp
        };
    }
  }

  private handleJavaScriptError(error: Error): AppError {
    return {
      code: 'JAVASCRIPT_ERROR',
      message: error.message,
      details: { stack: error.stack },
      timestamp: new Date()
    };
  }

  private handleUnknownError(error: unknown): AppError {
    const errorRecord = error as Record<string, unknown> | null;
    const errorMessage = errorRecord?.['message'];
    return {
      code: 'UNKNOWN_ERROR',
      message: typeof errorMessage === 'string' ? errorMessage : 'An unknown error occurred',
      details: error,
      timestamp: new Date()
    };
  }

  formatForDisplay(message: string): string {
    let formatted = message
      .replace(/at .*/gi, '')
      .replace(/in .*/gi, '')
      .replace(/\n/g, ' ')
      .trim();

    if (formatted.length > 0) {
      formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }

    return formatted;
  }
}
